import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
  CalendarDays,
  Plus,
  Trash2,
  Edit,
  Eye,
  EyeOff,
  Loader2,
  MapPin,
  Clock,
  Users,
  Link as LinkIcon,
  Upload,
  Image,
  Video,
  X,
  Copy,
  CheckCircle,
  XCircle,
  HelpCircle,
  FileText,
  Download,
} from 'lucide-react';
import api from '../../services/api';
import { useFeature } from '../../hooks/useFeature';
import { getBusinessTimezone, utcToLocalInputValue, localInputValueToUtc, formatDateTimeDisplay } from '../../utils/timezone';
import RichTextEditor from '../../components/RichTextEditor';

const API_URL = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL) || '';

// Strip HTML tags from rich-text descriptions for card excerpts so
// admins don't see literal `<p>` / `<strong>` in the list. The
// full HTML is preserved in the detail modal.
const htmlToPlainText = (html) => {
  if (!html) return '';
  try {
    return String(html)
      .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<\/(p|div|li|h[1-6])>/gi, ' ')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\s+/g, ' ')
      .trim();
  } catch {
    return String(html);
  }
};

// The list is a scan surface, not the event detail page. Bound excerpts
// in JavaScript so the complete rich-text body cannot force a card to grow
// when a line-clamp utility is unavailable in a tenant build.
const eventDescriptionExcerpt = (html, maxLength = 240) => {
  const text = htmlToPlainText(html);
  return text.length > maxLength ? `${text.slice(0, maxLength).trimEnd()}…` : text;
};

const EventsManagement = () => {
  // iter317 — Phase 7B plan gate. Surfaces the upgrade modal when the
  // tenant's plan does not include the `events.core` feature.
  const eventsFeature = useFeature('events.core');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [businessTimezone, setBusinessTimezone] = useState('America/New_York');
  
  // Create/Edit Dialog
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: 'private',
    start_datetime: '',
    end_datetime: '',
    is_multi_day: false,
    location: '',
    is_virtual: false,
    virtual_meeting_link: '',
    max_capacity: '',
    enable_waitlist: false,
    rsvp_deadline: '',
    dress_code: '',
    what_to_bring: '',
    cost: '',
    cost_description: '',
    send_notifications: true,
    notify_parents: true,
    notify_customers: false,
    notify_coaches: false,
    notify_staff: false,
    notify_admins: false,
  });
  
  // Media upload
  const [uploading, setUploading] = useState(false);
  
  // View RSVPs dialog
  const [viewingEvent, setViewingEvent] = useState(null);
  const [rsvps, setRsvps] = useState([]);
  const [loadingRsvps, setLoadingRsvps] = useState(false);
  
  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const init = async () => {
      const tz = await getBusinessTimezone(api);
      setBusinessTimezone(tz);
      fetchEvents();
    };
    init();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await api.get('/events/admin?include_inactive=true');
      setEvents(response.data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      event_type: 'private',
      start_datetime: '',
      end_datetime: '',
      is_multi_day: false,
      location: '',
      is_virtual: false,
      virtual_meeting_link: '',
      max_capacity: '',
      enable_waitlist: false,
      rsvp_deadline: '',
      dress_code: '',
      what_to_bring: '',
      cost: '',
      cost_description: '',
      send_notifications: true,
      show_on_homepage: false,
      notify_parents: true,
      notify_customers: false,
      notify_coaches: false,
      notify_staff: false,
      notify_admins: false,
    });
    setEditingEvent(null);
  };

  const openCreateDialog = () => {
    // iter317 — Phase 7B plan-gating. If the tenant's plan doesn't
    // include events, surface the upgrade modal instead of opening the
    // create form. The router-level gate would otherwise let the form
    // open then 402 on submit, which is a worse UX.
    if (!eventsFeature.enabled) {
      eventsFeature.prompt('Create an event');
      return;
    }
    resetForm();
    setShowEventDialog(true);
  };

  const openEditDialog = (event) => {
    setEditingEvent(event);
    // Convert UTC dates to business timezone for editing
    setFormData({
      title: event.title || '',
      description: event.description || '',
      event_type: event.event_type || 'private',
      start_datetime: utcToLocalInputValue(event.start_datetime, businessTimezone),
      end_datetime: utcToLocalInputValue(event.end_datetime, businessTimezone),
      is_multi_day: event.is_multi_day || false,
      location: event.location || '',
      is_virtual: event.is_virtual || false,
      virtual_meeting_link: event.virtual_meeting_link || '',
      max_capacity: event.max_capacity || '',
      enable_waitlist: event.enable_waitlist || false,
      rsvp_deadline: utcToLocalInputValue(event.rsvp_deadline, businessTimezone),
      dress_code: event.dress_code || '',
      what_to_bring: event.what_to_bring || '',
      cost: event.cost || '',
      cost_description: event.cost_description || '',
      send_notifications: event.send_notifications ?? true,
      show_on_homepage: event.show_on_homepage || false,
      notify_parents: event.notify_parents ?? false,
      notify_customers: event.notify_customers ?? false,
      notify_coaches: event.notify_coaches ?? false,
      notify_staff: event.notify_staff ?? false,
      notify_admins: event.notify_admins ?? false,
    });
    setShowEventDialog(true);
  };

  const handleSaveEvent = async () => {
    if (!formData.title.trim()) {
      toast.error('Event title is required');
      return;
    }
    if (!formData.start_datetime || !formData.end_datetime) {
      toast.error('Start and end date/time are required');
      return;
    }
    
    setSaving(true);
    try {
      // Convert business timezone dates to UTC for storage
      const payload = {
        ...formData,
        start_datetime: localInputValueToUtc(formData.start_datetime, businessTimezone),
        end_datetime: localInputValueToUtc(formData.end_datetime, businessTimezone),
        rsvp_deadline: formData.rsvp_deadline ? localInputValueToUtc(formData.rsvp_deadline, businessTimezone) : null,
        max_capacity: formData.max_capacity ? parseInt(formData.max_capacity) : null,
        cost: formData.cost ? parseFloat(formData.cost) : null
      };
      
      if (editingEvent) {
        // iter320 — prompt admin whether to notify recipients about the update.
        // Only prompt if at least one recipient role is enabled on the event.
        const anyRole =
          payload.notify_parents ||
          payload.notify_customers ||
          payload.notify_coaches ||
          payload.notify_staff ||
          payload.notify_admins;
        let sendUpdateEmail = false;
        if (anyRole) {
          sendUpdateEmail = window.confirm(
            'Send an update email to the selected recipient roles for this event?\n\n' +
            'OK = send update email  ·  Cancel = save changes without emailing'
          );
        }
        await api.put(
          `/events/${editingEvent.id}${sendUpdateEmail ? '?send_update_email=true' : ''}`,
          payload
        );
        toast.success(
          sendUpdateEmail
            ? 'Event updated · update emails queued'
            : 'Event updated successfully'
        );
      } else {
        await api.post('/events', payload);
        const anyRole =
          payload.notify_parents ||
          payload.notify_customers ||
          payload.notify_coaches ||
          payload.notify_staff ||
          payload.notify_admins;
        toast.success(
          anyRole
            ? 'Event created · invitation emails queued'
            : 'Event created successfully'
        );
      }
      
      setShowEventDialog(false);
      resetForm();
      fetchEvents();
    } catch (error) {
      console.error('Error saving event:', error);
      toast.error(error.response?.data?.detail || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (event) => {
    try {
      await api.put(`/events/${event.id}`, { is_active: !event.is_active });
      toast.success(`Event ${event.is_active ? 'deactivated' : 'activated'}`);
      fetchEvents();
    } catch (error) {
      toast.error('Failed to update event');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    
    setDeleting(true);
    try {
      await api.delete(`/events/${deleteConfirm.id}`);
      toast.success('Event deleted');
      setDeleteConfirm(null);
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete event');
    } finally {
      setDeleting(false);
    }
  };

  const handleMediaUpload = async (event, file) => {
    if (!file) return;
    
    const isVideo = file.type.startsWith('video/');
    const maxSize = isVideo ? 100 * 1024 * 1024 : 10 * 1024 * 1024;
    
    if (file.size > maxSize) {
      toast.error(`File must be less than ${isVideo ? '100MB' : '10MB'}`);
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await api.post(`/events/${event.id}/media`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`${isVideo ? 'Video' : 'Image'} uploaded successfully`);
      // Refresh list and, if the edit dialog is open for this event,
      // refresh its snapshot so the new thumbnail appears immediately.
      const refreshed = await api.get(`/events/${event.id}`);
      if (editingEvent && editingEvent.id === event.id && refreshed?.data) {
        setEditingEvent(refreshed.data);
      }
      fetchEvents();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload media');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteMedia = async (eventId, mediaId) => {
    try {
      await api.delete(`/events/${eventId}/media/${mediaId}`);
      toast.success('Media deleted');
      const refreshed = await api.get(`/events/${eventId}`);
      if (editingEvent && editingEvent.id === eventId && refreshed?.data) {
        setEditingEvent(refreshed.data);
      }
      fetchEvents();
    } catch (error) {
      toast.error('Failed to delete media');
    }
  };

  const openRsvpDialog = async (event) => {
    setViewingEvent(event);
    setLoadingRsvps(true);
    try {
      const response = await api.get(`/events/${event.id}/rsvps`);
      setRsvps(response.data.rsvps || []);
    } catch (error) {
      toast.error('Failed to load RSVPs');
      setRsvps([]);
    } finally {
      setLoadingRsvps(false);
    }
  };

  const handleExportRsvps = async (eventId) => {
    try {
      const response = await api.post(`/events/${eventId}/export-rsvps`, {}, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `rsvps_${eventId.slice(0, 8)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('RSVPs exported');
    } catch (error) {
      toast.error('Failed to export RSVPs');
    }
  };

  const copyRsvpLink = async (event) => {
    try {
      let token = event.public_rsvp_token;
      if (!token) {
        const response = await api.post(`/events/${event.id}/rsvp-link`);
        token = response.data?.token;
        if (token) {
          setEvents((current) => current.map((item) => (
            item.id === event.id ? { ...item, public_rsvp_token: token } : item
          )));
        }
      }
      if (!token) throw new Error('Missing RSVP token');
      await navigator.clipboard.writeText(`${window.location.origin}/events/rsvp/${token}`);
      toast.success('RSVP link copied to clipboard');
    } catch (error) {
      console.error('Unable to copy RSVP link:', error);
      toast.error('Could not create the RSVP link');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'yes':
        return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Yes</Badge>;
      case 'no':
        return <Badge className="bg-red-500"><XCircle className="w-3 h-3 mr-1" />No</Badge>;
      case 'maybe':
        return <Badge className="bg-yellow-500"><HelpCircle className="w-3 h-3 mr-1" />Maybe</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    // Use business timezone for display
    return formatDateTimeDisplay(isoString, businessTimezone);
  };

  // Filter events by tab
  const now = new Date();
  const filteredEvents = events.filter(event => {
    const eventEnd = new Date(event.end_datetime);
    if (activeTab === 'upcoming') {
      return eventEnd >= now && event.is_active;
    } else if (activeTab === 'past') {
      return eventEnd < now && event.is_active;
    } else {
      return !event.is_active;
    }
  });

  if (loading) {
    return (
      <DashboardLayout title="Events">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Event Invitations">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Event Invitations</h1>
            <p className="text-slate-600">Create and manage academy events</p>
          </div>
          <Button onClick={openCreateDialog} data-testid="create-event-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Event
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="upcoming" data-testid="tab-upcoming">
              Upcoming ({events.filter(e => new Date(e.end_datetime) >= now && e.is_active).length})
            </TabsTrigger>
            <TabsTrigger value="past" data-testid="tab-past">
              Past ({events.filter(e => new Date(e.end_datetime) < now).length})
            </TabsTrigger>
            <TabsTrigger value="inactive" data-testid="tab-inactive">
              Inactive ({events.filter(e => !e.is_active).length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-4">
            {filteredEvents.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12 text-slate-500">
                  <CalendarDays className="w-16 h-16 mb-4 text-slate-300" />
                  <p className="text-lg font-medium">No {activeTab} events</p>
                  <p className="text-sm mt-1">
                    {activeTab === 'upcoming' ? 'Create your first event to get started' : `No ${activeTab} events found`}
                  </p>
                  {activeTab === 'upcoming' && (
                    <Button className="mt-4" onClick={openCreateDialog}>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Event
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {filteredEvents.map(event => (
                  <EventCard
                    key={event.id}
                    event={event}
                    onEdit={() => openEditDialog(event)}
                    onToggleActive={() => handleToggleActive(event)}
                    onDelete={() => setDeleteConfirm(event)}
                    onViewRsvps={() => openRsvpDialog(event)}
                    onCopyLink={() => copyRsvpLink(event)}
                    onMediaUpload={(file) => handleMediaUpload(event, file)}
                    onDeleteMedia={(mediaId) => handleDeleteMedia(event.id, mediaId)}
                    uploading={uploading}
                    formatDateTime={formatDateTime}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Create/Edit Event Dialog */}
      <Dialog open={showEventDialog} onOpenChange={(open) => { if (!open) resetForm(); setShowEventDialog(open); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingEvent ? 'Edit Event' : 'Create New Event'}</DialogTitle>
            <DialogDescription>
              {editingEvent ? 'Update event details' : 'Fill in the details to create a new event'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="space-y-4">
              <div>
                <Label>Event Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Annual Cricket Tournament Celebration"
                  className="mt-1"
                  data-testid="event-title-input"
                />
              </div>
              
              <div>
                <Label>Description</Label>
                <div className="mt-1">
                  <RichTextEditor
                    value={formData.description}
                    onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                    placeholder="Describe the event..."
                    minHeight="150px"
                    maxHeight="300px"
                  />
                </div>
              </div>
              
              <div>
                <Label>Event Visibility</Label>
                <Select
                  value={formData.event_type}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, event_type: value }))}
                >
                  <SelectTrigger className="mt-1" data-testid="event-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="private">Private (Enrolled Parents Only)</SelectItem>
                    <SelectItem value="public">Public (Anyone can RSVP)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  {formData.event_type === 'public' 
                    ? 'A shareable link will be generated for public RSVPs' 
                    : 'Only logged-in parents can see and RSVP to this event'}
                </p>
              </div>
              
              {/* Show on Homepage toggle - only for public events */}
              {formData.event_type === 'public' && (
                <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <Switch
                    checked={formData.show_on_homepage}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, show_on_homepage: checked }))}
                    data-testid="show-on-homepage-toggle"
                  />
                  <div>
                    <Label className="font-medium">Show on Homepage</Label>
                    <p className="text-xs text-blue-600">Display this event prominently on the academy website homepage</p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Date/Time */}
            <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
              <h4 className="font-medium flex items-center gap-2">
                <Clock className="w-4 h-4" /> Date & Time
              </h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date/Time *</Label>
                  <Input
                    type="datetime-local"
                    value={formData.start_datetime}
                    onChange={(e) => setFormData(prev => ({ ...prev, start_datetime: e.target.value }))}
                    className="mt-1"
                    data-testid="event-start-input"
                  />
                </div>
                <div>
                  <Label>End Date/Time *</Label>
                  <Input
                    type="datetime-local"
                    value={formData.end_datetime}
                    onChange={(e) => setFormData(prev => ({ ...prev, end_datetime: e.target.value }))}
                    className="mt-1"
                    data-testid="event-end-input"
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_multi_day}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_multi_day: checked }))}
                />
                <Label>Multi-day event</Label>
              </div>
            </div>
            
            {/* Location */}
            <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
              <h4 className="font-medium flex items-center gap-2">
                <MapPin className="w-4 h-4" /> Location
              </h4>
              
              <div>
                <Label>Location Address</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="e.g., Main Cricket Ground, 123 Sports Ave"
                  className="mt-1"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_virtual}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_virtual: checked }))}
                />
                <Label>Virtual/Online Event</Label>
              </div>
              
              {formData.is_virtual && (
                <div>
                  <Label>Meeting Link</Label>
                  <Input
                    value={formData.virtual_meeting_link}
                    onChange={(e) => setFormData(prev => ({ ...prev, virtual_meeting_link: e.target.value }))}
                    placeholder="https://zoom.us/j/..."
                    className="mt-1"
                  />
                </div>
              )}
            </div>
            
            {/* Capacity */}
            <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
              <h4 className="font-medium flex items-center gap-2">
                <Users className="w-4 h-4" /> Capacity & RSVP
              </h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Max Capacity</Label>
                  <Input
                    type="number"
                    value={formData.max_capacity}
                    onChange={(e) => setFormData(prev => ({ ...prev, max_capacity: e.target.value }))}
                    placeholder="Leave empty for unlimited"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>RSVP Deadline</Label>
                  <Input
                    type="datetime-local"
                    value={formData.rsvp_deadline}
                    onChange={(e) => setFormData(prev => ({ ...prev, rsvp_deadline: e.target.value }))}
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.enable_waitlist}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, enable_waitlist: checked }))}
                />
                <Label>Enable waitlist when capacity is reached</Label>
              </div>
            </div>
            
            {/* Additional Info */}
            <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
              <h4 className="font-medium flex items-center gap-2">
                <FileText className="w-4 h-4" /> Additional Details
              </h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Event Cost</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.cost}
                    onChange={(e) => setFormData(prev => ({ ...prev, cost: e.target.value }))}
                    placeholder="0.00 (free)"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Cost Description</Label>
                  <Input
                    value={formData.cost_description}
                    onChange={(e) => setFormData(prev => ({ ...prev, cost_description: e.target.value }))}
                    placeholder="e.g., Includes lunch"
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div>
                <Label>Dress Code</Label>
                <Input
                  value={formData.dress_code}
                  onChange={(e) => setFormData(prev => ({ ...prev, dress_code: e.target.value }))}
                  placeholder="e.g., Cricket whites, Casual"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>What to Bring</Label>
                <Textarea
                  value={formData.what_to_bring}
                  onChange={(e) => setFormData(prev => ({ ...prev, what_to_bring: e.target.value }))}
                  placeholder="e.g., Cricket bat, Water bottle, Sunscreen"
                  className="mt-1"
                  rows={2}
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.send_notifications}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, send_notifications: checked }))}
                />
                <Label>Send RSVP confirmation emails</Label>
              </div>

              {/* iter320 — per-role invitation targeting */}
              <div
                className="p-4 bg-white border border-slate-200 rounded-lg space-y-3"
                data-testid="event-dialog-notify-section"
              >
                <div>
                  <Label className="text-sm font-semibold text-slate-800">
                    Send invitation email to
                  </Label>
                  <p className="text-xs text-slate-500 mt-1">
                    Parents = users with an active or paused enrollment · everyone else = all active users of that role.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: 'notify_parents', label: 'Parents' },
                    { key: 'notify_customers', label: 'Customers' },
                    { key: 'notify_coaches', label: 'Coaches' },
                    { key: 'notify_staff', label: 'Staff' },
                    { key: 'notify_admins', label: 'Admins' },
                  ].map(({ key, label }) => (
                    <label
                      key={key}
                      className="flex items-center gap-2 cursor-pointer"
                      data-testid={`event-dialog-${key.replace('_', '-')}-label`}
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300"
                        checked={!!formData[key]}
                        onChange={(e) => setFormData(prev => ({ ...prev, [key]: e.target.checked }))}
                        data-testid={`event-dialog-${key.replace('_', '-')}-checkbox`}
                      />
                      <span className="text-sm text-slate-700">{label}</span>
                    </label>
                  ))}
                </div>
                {editingEvent && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                    You&apos;ll be asked whether to send an update email when you save changes.
                  </p>
                )}
              </div>
            </div>

            {/* Media (images / video) */}
            <div className="space-y-4 p-4 bg-slate-50 rounded-lg" data-testid="event-dialog-media-section">
              <h4 className="font-medium flex items-center gap-2">
                <Image className="w-4 h-4" /> Photos & Video
              </h4>

              {editingEvent ? (
                <EventDialogMediaPanel
                  event={editingEvent}
                  onMediaUpload={(file) => handleMediaUpload(editingEvent, file)}
                  onDeleteMedia={(mediaId) => handleDeleteMedia(editingEvent.id, mediaId)}
                  uploading={uploading}
                />
              ) : (
                <div
                  className="text-sm text-slate-600 bg-white border border-dashed border-slate-300 rounded-md p-4"
                  data-testid="event-dialog-media-hint"
                >
                  <p className="font-medium text-slate-700 mb-1">Media upload becomes available after you save the event.</p>
                  <p className="text-slate-500">
                    Click <span className="font-semibold">Create Event</span> below, then reopen this event from the list to add images (up to 10 MB each) or a single video (up to 100 MB).
                  </p>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEventDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEvent} disabled={saving} data-testid="save-event-btn">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                editingEvent ? 'Save Changes' : 'Create Event'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View RSVPs Dialog */}
      <Dialog open={!!viewingEvent} onOpenChange={(open) => !open && setViewingEvent(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>RSVPs for {viewingEvent?.title}</DialogTitle>
            <DialogDescription>
              {viewingEvent?.rsvp_counts && (
                <span className="flex gap-4 mt-2">
                  <Badge className="bg-green-500">Yes: {viewingEvent.rsvp_counts.yes?.count || 0}</Badge>
                  <Badge className="bg-yellow-500">Maybe: {viewingEvent.rsvp_counts.maybe?.count || 0}</Badge>
                  <Badge className="bg-red-500">No: {viewingEvent.rsvp_counts.no?.count || 0}</Badge>
                  {viewingEvent.rsvp_counts.waitlist > 0 && (
                    <Badge variant="outline">Waitlist: {viewingEvent.rsvp_counts.waitlist}</Badge>
                  )}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {loadingRsvps ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
              </div>
            ) : rsvps.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>No RSVPs yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {rsvps.map(rsvp => (
                  <div key={rsvp.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium">{rsvp.user_name}</p>
                      <p className="text-sm text-slate-500">{rsvp.user_email}</p>
                      {(rsvp.num_guests > 0 || rsvp.num_children > 0) && (
                        <p className="text-xs text-slate-400 mt-1">
                          +{rsvp.num_guests} guest(s), {rsvp.num_children} child(ren)
                        </p>
                      )}
                      {rsvp.notes && <p className="text-xs text-slate-500 mt-1 italic">"{rsvp.notes}"</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      {rsvp.is_waitlisted && <Badge variant="outline">Waitlisted</Badge>}
                      {getStatusBadge(rsvp.status)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => handleExportRsvps(viewingEvent?.id)}>
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button onClick={() => setViewingEvent(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Event</DialogTitle>
            <DialogDescription>
              Delete "{deleteConfirm?.title}" permanently? This is available only when the event has no RSVPs. Events with RSVP history must remain inactive so the history is retained.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Event
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

// Event Card Component
const EventCard = ({ 
  event, 
  onEdit, 
  onToggleActive, 
  onDelete, 
  onViewRsvps, 
  onCopyLink,
  onMediaUpload,
  onDeleteMedia,
  uploading,
  formatDateTime 
}) => {
  const [showMediaUpload, setShowMediaUpload] = useState(false);
  const fileInputRef = React.useRef(null);
  
  const images = (event.media || []).filter(m => m.type === 'image');
  const video = (event.media || []).find(m => m.type === 'video');
  
  const totalAttending = event.rsvp_counts?.total_attending || 0;
  const capacityPercent = event.max_capacity 
    ? Math.min(100, (totalAttending / event.max_capacity) * 100)
    : 0;

  return (
    <Card className={`${!event.is_active ? 'opacity-60' : ''}`} data-testid={`event-card-${event.id}`}>
      <CardContent className="p-0">
        <div className="flex flex-col lg:flex-row">
          {/* Media Section */}
          <div className="lg:w-1/3 bg-slate-100 relative">
            {images.length > 0 || video ? (
              <div className="aspect-video lg:aspect-square">
                {images.length > 0 ? (
                  <img
                    src={`${API_URL}/api/events/${event.id}/media/${images[0].id}`}
                    alt={event.title}
                    className="w-full h-full object-cover"
                  />
                ) : video ? (
                  <video
                    src={`${API_URL}/api/events/${event.id}/media/${video.id}`}
                    className="w-full h-full object-cover"
                    controls
                  />
                ) : null}
                
                {/* Media count badge */}
                {(images.length > 1 || video) && (
                  <div className="absolute bottom-2 right-2 flex gap-1">
                    {images.length > 1 && (
                      <Badge className="bg-black/70">
                        <Image className="w-3 h-3 mr-1" />
                        {images.length}
                      </Badge>
                    )}
                    {video && (
                      <Badge className="bg-black/70">
                        <Video className="w-3 h-3" />
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="aspect-video lg:aspect-square flex items-center justify-center">
                <CalendarDays className="w-16 h-16 text-slate-300" />
              </div>
            )}
            
            {/* Upload button */}
            <Button
              variant="default"
              size="sm"
              className="absolute top-2 right-2 shadow-md"
              onClick={() => setShowMediaUpload(!showMediaUpload)}
              data-testid={`event-media-upload-btn-${event.id}`}
            >
              <Upload className="w-4 h-4 mr-1" />
              {(images.length > 0 || video) ? 'Manage Media' : 'Upload Media'}
            </Button>
            
            {/* Media upload panel */}
            {showMediaUpload && (
              <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center p-4 gap-3">
                <p className="text-white text-sm text-center">Upload images or video</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*"
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      onMediaUpload(e.target.files[0]);
                      setShowMediaUpload(false);
                    }
                  }}
                  className="hidden"
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                    Add Media
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-white"
                    onClick={() => setShowMediaUpload(false)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                
                {/* Existing media thumbnails with delete */}
                {(images.length > 0 || video) && (
                  <div className="flex gap-2 flex-wrap mt-2">
                    {images.map(img => (
                      <div key={img.id} className="relative group">
                        <img
                          src={`${API_URL}/api/events/${event.id}/media/${img.id}`}
                          alt=""
                          className="w-12 h-12 object-cover rounded"
                        />
                        <button
                          onClick={() => onDeleteMedia(img.id)}
                          className="absolute -top-1 -right-1 bg-red-500 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3 text-white" />
                        </button>
                      </div>
                    ))}
                    {video && (
                      <div className="relative group">
                        <div className="w-12 h-12 bg-slate-700 rounded flex items-center justify-center">
                          <Video className="w-6 h-6 text-white" />
                        </div>
                        <button
                          onClick={() => onDeleteMedia(video.id)}
                          className="absolute -top-1 -right-1 bg-red-500 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3 text-white" />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Content Section */}
          <div className="lg:w-2/3 p-4 sm:p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <Badge className={event.event_type === 'public' ? 'bg-blue-500' : 'bg-slate-500'}>
                    {event.event_type === 'public' ? 'Public' : 'Private'}
                  </Badge>
                  {event.show_on_homepage && <Badge className="bg-amber-500">Featured</Badge>}
                  {!event.is_active && <Badge variant="outline">Inactive</Badge>}
                  {event.is_virtual && <Badge variant="outline"><LinkIcon className="w-3 h-3 mr-1" />Virtual</Badge>}
                </div>
                <h3 className="text-lg font-semibold text-slate-900 truncate">{event.title}</h3>
                {event.description && (
                  <p className="text-sm text-slate-500 mt-1">{eventDescriptionExcerpt(event.description)}</p>
                )}
              </div>
            </div>
            
            {/* Event Details */}
            <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
              <div className="flex items-center gap-2 text-slate-600">
                <Clock className="w-4 h-4 text-slate-400" />
                <span>{formatDateTime(event.start_datetime)}</span>
              </div>
              {event.location && (
                <div className="flex items-center gap-2 text-slate-600">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  <span className="truncate">{event.location}</span>
                </div>
              )}
            </div>
            
            {/* Capacity Bar */}
            {event.max_capacity && (
              <div className="mt-4">
                <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                  <span>Capacity</span>
                  <span>{totalAttending} / {event.max_capacity}</span>
                </div>
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${capacityPercent >= 100 ? 'bg-red-500' : capacityPercent >= 80 ? 'bg-yellow-500' : 'bg-green-500'}`}
                    style={{ width: `${capacityPercent}%` }}
                  />
                </div>
              </div>
            )}
            
            {/* RSVP Summary - Two Groups */}
            <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
              {/* RSVP Responses */}
              <div>
                <span className="text-slate-500 text-xs font-medium uppercase tracking-wide">Responses</span>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline" className="text-green-600">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    {event.rsvp_counts?.yes?.count || 0}
                  </Badge>
                  <Badge variant="outline" className="text-yellow-600">
                    <HelpCircle className="w-3 h-3 mr-1" />
                    {event.rsvp_counts?.maybe?.count || 0}
                  </Badge>
                  <Badge variant="outline" className="text-red-600">
                    <XCircle className="w-3 h-3 mr-1" />
                    {event.rsvp_counts?.no?.count || 0}
                  </Badge>
                </div>
              </div>
              
              {/* Guest Counts */}
              <div>
                <span className="text-slate-500 text-xs font-medium uppercase tracking-wide">Guest Count</span>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline" className="text-blue-600">
                    <Users className="w-3 h-3 mr-1" />
                    {(event.rsvp_counts?.yes?.count || 0) + (event.rsvp_counts?.yes?.guests || 0)} adults
                  </Badge>
                  <Badge variant="outline" className="text-purple-600">
                    {event.rsvp_counts?.yes?.children || 0} kids
                  </Badge>
                </div>
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex items-center gap-2 mt-4 pt-4 border-t flex-wrap">
              <Button variant="outline" size="sm" onClick={onViewRsvps}>
                <Users className="w-4 h-4 mr-1" />
                View RSVPs
              </Button>
              <Button variant="outline" size="sm" onClick={onCopyLink}>
                <Copy className="w-4 h-4 mr-1" />
                Copy RSVP Link
              </Button>
              <Button variant="outline" size="sm" onClick={onToggleActive}>
                {event.is_active ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                {event.is_active ? 'Deactivate' : 'Activate'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onEdit}
                data-testid={`event-edit-btn-${event.id}`}
              >
                <Edit className="w-4 h-4 mr-1" />
                Edit
              </Button>
              <Button variant="outline" size="sm" onClick={onDelete} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default EventsManagement;

// -----------------------------------------------------------------
// Media upload panel used inside the Create/Edit dialog (edit mode).
// Reuses the same handlers wired in the parent component.
// -----------------------------------------------------------------
const EventDialogMediaPanel = ({ event, onMediaUpload, onDeleteMedia, uploading }) => {
  const fileInputRef = React.useRef(null);
  const images = (event.media || []).filter((m) => m.type === 'image');
  const video = (event.media || []).find((m) => m.type === 'video');

  return (
    <div className="space-y-3">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*"
        onChange={(e) => {
          if (e.target.files && e.target.files[0]) {
            onMediaUpload(e.target.files[0]);
            e.target.value = '';
          }
        }}
        className="hidden"
        data-testid="event-dialog-media-file-input"
      />

      <div className="flex items-center gap-2 flex-wrap">
        <Button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          data-testid="event-dialog-media-add-btn"
        >
          {uploading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Uploading…
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Add Image or Video
            </>
          )}
        </Button>
        <span className="text-xs text-slate-500">
          Images up to 10 MB (JPEG/PNG/WebP/GIF) · one video up to 100 MB (MP4/WebM/MOV)
        </span>
      </div>

      {(images.length > 0 || video) ? (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {images.map((img) => (
            <div
              key={img.id}
              className="relative group aspect-square rounded overflow-hidden border border-slate-200"
              data-testid={`event-dialog-media-thumb-${img.id}`}
            >
              <img
                src={`${API_URL}/api/events/${event.id}/media/${img.id}`}
                alt=""
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => onDeleteMedia(img.id)}
                className="absolute top-1 right-1 bg-red-500 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`event-dialog-media-delete-${img.id}`}
              >
                <X className="w-3 h-3 text-white" />
              </button>
            </div>
          ))}
          {video && (
            <div
              className="relative group aspect-square rounded overflow-hidden border border-slate-200 bg-slate-800 flex items-center justify-center"
              data-testid={`event-dialog-media-thumb-${video.id}`}
            >
              <Video className="w-8 h-8 text-white" />
              <button
                type="button"
                onClick={() => onDeleteMedia(video.id)}
                className="absolute top-1 right-1 bg-red-500 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`event-dialog-media-delete-${video.id}`}
              >
                <X className="w-3 h-3 text-white" />
              </button>
            </div>
          )}
        </div>
      ) : (
        <p className="text-sm text-slate-500 italic">No media uploaded yet.</p>
      )}
    </div>
  );
};
